import { authProtectedRoutes, profileProtectedRoute } from '@/routes/menu-route';
import type { MerchantRoleAccess } from '@/stores/role-access';
import type { RouteConfig } from '@/types/routeConfig';

export const flattenRoutes = (routes: RouteConfig[]): RouteConfig[] => {
  return routes.flatMap((route) => {
    const current = { path: route.path, component: route.component };
    const children = route.children ? flattenRoutes(route.children) : [];
    return [current, ...children];
  });
};

export const checkDashboardAccess = (() => {
  // getAccessibleRoutes and buildAllowedRouteSet as Private helper
  function getAccessibleRoutes(roleAccess: MerchantRoleAccess): string[] {
    if (!roleAccess) return [];

    // implement single pass loop (O(1)) instead of multiple flatMap or filter function
    const { sidebarRoutes, roleActionSet } = roleAccess.access.reduce(
      (acc, group) => {
        group.role_access.forEach((ra) => {
          if (ra.route) acc.sidebarRoutes.push(ra.route);
          (ra.action ?? []).forEach((act) => acc.roleActionSet.add(act.toLowerCase()));
        });
        return acc;
      },
      { sidebarRoutes: [] as string[], roleActionSet: new Set<string>() },
    );

    // Filter leftover actions in single pass (O(n))
    const hiddenRoutes = Object.keys(roleAccess.action)
      .filter((key) => !roleActionSet.has(key.toLowerCase()))
      .filter((key) => key.endsWith('_read_table'))
      .map((key) => '/' + key.replace(/_read_table$/, '').replace(/_/g, '-'));

    // Merge into a Set to dedupe (O(n))
    return Array.from(new Set([...sidebarRoutes, ...hiddenRoutes]));
  }

  function buildAllowedRouteSet(accessibleRoutes: string[]): Set<string> {
    const baseSet = new Set(accessibleRoutes);

    const allowed = authProtectedRoutes.reduce((acc, route) => {
      if (baseSet.has(route) || [...baseSet].some((base) => route.startsWith(base + '/'))) {
        acc.add(route);
      }
      return acc;
    }, new Set<string>());

    profileProtectedRoute.forEach((pr) => allowed.add(pr));
    return allowed;
  }

  // Public function (encapsulation)
  return function checkDashboardAccess(
    pathname: string,
    roleAccess: MerchantRoleAccess | null,
  ): boolean {
    if (!roleAccess) return false;
    const accessibleRoutes = getAccessibleRoutes(roleAccess);
    const allowedSet = buildAllowedRouteSet(accessibleRoutes);

    // Exact match or subpath match
    return [...allowedSet].some((route) => pathname === route || pathname.startsWith(route + '/'));
  };
})();
